import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Breadcrumb, Button } from 'antd';
import { Link, useNavigate } from 'react-router';

import {
  useDeleteSchoolFAQMutation,
  useGetSchoolFAQsQuery,
  useReorderSchoolFAQsMutation,
} from '@/api/endpoints/schools/schoolSubjectionParams/FAQs';
import { DeleteConfirmationModal, DraggableTable, FetchDataWrapper } from '@/shared/components';
import { ROUTES, TABLE_TRANSLATIONS } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { CreateQuestionModal } from './components/Modals/CreateQuestionModal';
import { EditQuestionModal } from './components/Modals/EditQuestionModal';
import { getColumnsData } from './helpers';
import styles from './SchoolFAQSubsection.module.scss';
import type { IFAQItemData } from './types';

type TProps = {
  id: number;
  name: string;
};

export const SchoolFAQSubsection = ({ id, name }: TProps) => {
  const navigate = useNavigate();

  const {
    data: questionsData,
    isLoading: areQuestionsLoading,
    isFetching: areQuestionsFetching,
    isError: isQuestionsFetchError,
  } = useGetSchoolFAQsQuery({ schoolId: id });
  const [reorderQuestions] = useReorderSchoolFAQsMutation();
  const [deleteQuestion, { isLoading: isQuestionDeleting }] = useDeleteSchoolFAQMutation();

  const [tableData, setTableData] = useState<IFAQItemData[]>([]);

  const [isCreateQuestionModalOpened, setIsCreateQuestionModalOpened] = useState(false);
  const [isEditQuestionModalOpened, setIsEditQuestionModalOpened] = useState(false);
  const [isDeleteQuestionModalOpened, setIsDeleteQuestionModalOpened] = useState(false);

  const [currentQuestionItem, setCurrentQuestionItem] = useState<null | IFAQItemData>(null);

  const openCreateQuestionModal = useCallback(() => setIsCreateQuestionModalOpened(true), []);
  const closeCreateQuestionModal = useCallback(() => setIsCreateQuestionModalOpened(false), []);

  const openEditQuestionModal = useCallback(() => setIsEditQuestionModalOpened(true), []);
  const closeEditQuestionModal = useCallback(() => setIsEditQuestionModalOpened(false), []);

  const openDeleteQuestionModal = useCallback(() => setIsDeleteQuestionModalOpened(true), []);
  const closeDeleteQuestionModal = useCallback(() => setIsDeleteQuestionModalOpened(false), []);

  const sendApiRequest = useApiRequest();

  const setItemToOperate = useCallback((data: IFAQItemData | null) => setCurrentQuestionItem(data), []);

  const handleWithClearOperateItem = (handleFunction?: VoidFunction) => {
    handleFunction?.();
    setItemToOperate(null);
  };

  const deleteQuestionHandle = useCallback(() => {
    if (currentQuestionItem) {
      sendApiRequest({
        successMessage: 'Вопрос успешно удалён',
        apiRequestCallback: () =>
          deleteQuestion({
            schoolId: id,
            questionId: currentQuestionItem?.id,
          }),
        successCallback: () => handleWithClearOperateItem(closeDeleteQuestionModal),
      });
    }
  }, [currentQuestionItem?.id, id]);

  const columns = useMemo(
    () => getColumnsData({ openEditQuestionModal, openDeleteQuestionModal, setItemToOperate }),
    [openEditQuestionModal, openDeleteQuestionModal],
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!tableData || !over) {
      return;
    }

    if (active.id !== over.id) {
      const activeIndex = tableData.findIndex((i) => i.position === active.id);
      const overIndex = tableData.findIndex((i) => i.position === over.id);

      const reorderedArray = arrayMove(tableData, activeIndex, overIndex);

      setTableData(reorderedArray);

      const reorderedArrayIds = reorderedArray.map((question) => question.id);

      sendApiRequest({
        successMessage: 'Порядок вопросов успешно изменён',
        apiRequestCallback: () => reorderQuestions({ schoolId: id, data: { order: reorderedArrayIds } }),
        errorCallback: () => setTableData(tableData),
      });
    }
  };

  useEffect(() => {
    setTableData(questionsData ?? []);
  }, [questionsData]);

  return (
    <FetchDataWrapper
      isLoading={areQuestionsLoading}
      isError={isQuestionsFetchError}
    >
      {questionsData && (
        <div className={styles.wrapper}>
          <Breadcrumb
            items={[
              { title: 'Публичные страницы' },
              { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`}>Школы</Link> },
              { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${id}/${ROUTES.EDIT}`}>{name}</Link> },
              { title: 'Вопросы и ответы' },
            ]}
          />
          <div className={styles['add-button-wrapper']}>
            <Button
              type="primary"
              onClick={openCreateQuestionModal}
              className={styles.button}
            >
              Добавить вопрос
            </Button>
          </div>
          <DraggableTable
            onDragEnd={onDragEnd}
            keysForSorting={tableData.map((i) => i.position)}
            columns={columns}
            rowKey="position"
            size="large"
            locale={TABLE_TRANSLATIONS}
            pagination={false}
            dataSource={tableData}
            loading={areQuestionsFetching}
          />
          <div className={styles['action-buttons-wrapper']}>
            <Button
              onClick={() => navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${id}/${ROUTES.EDIT}`)}
              type="dashed"
              size="large"
              className={styles.button}
            >
              Назад
            </Button>
          </div>
          {isDeleteQuestionModalOpened && (
            <DeleteConfirmationModal
              isModalOpen={isDeleteQuestionModalOpened}
              description="Вы уверены, что хотите удалить вопрос?"
              title="Удаление вопроса"
              handleCancel={() => handleWithClearOperateItem(closeDeleteQuestionModal)}
              handleOk={deleteQuestionHandle}
              itemToOperate={currentQuestionItem}
              shouldDisableElement={isQuestionDeleting}
            />
          )}
          {isCreateQuestionModalOpened && (
            <CreateQuestionModal
              isModalOpen={isCreateQuestionModalOpened}
              handleCancel={closeCreateQuestionModal}
              schoolId={id}
            />
          )}
          {isEditQuestionModalOpened && currentQuestionItem && (
            <EditQuestionModal
              isModalOpen={isEditQuestionModalOpened}
              handleCancel={() => handleWithClearOperateItem(closeEditQuestionModal)}
              handleClearOperateItem={() => handleWithClearOperateItem()}
              itemToOperate={currentQuestionItem}
              schoolId={id}
            />
          )}
        </div>
      )}
    </FetchDataWrapper>
  );
};
